"use client";

import { API_ENDPOINT } from "@/constants/appConstants";
import {
  RegistrationContext,
  useRegistrationProps,
} from "@/context/useRegistrationProps";
import trackPerformance from "@/utils/performance";
import trackClevertapEvents from "@/utils/trackClevertapEvents";
import translation from "@/utils/translation_registration.json";
import { Button, Toast } from "@monsterindia/global-ui-components";
import "@monsterindia/global-ui-components/dist/styles.css";
import Image from "next/image";
import React, { Suspense, useEffect, useMemo, useState } from "react";

import { regFlow, sprFlow, sprWithJobIdFlow } from "../../constants/common";
import { customTwMerge as twMerge } from "../../helpers/customTwMerge";
import { useMobile, useNetwork } from "../../hooks";
import { AddDetailsAPIResDataType } from "../../types/addDetailsApi";
import { EventTrackingProps } from "../../types/common";
import { Details } from "../../types/userDetails";
import Header from "../common/RegistrationHeader";
import "../common/common.css";
import { regMap } from "./map";
import ProgressBar from "./progress-bar";
import SignUp from "./signup";
import Skeleton from "./skeleton";

const cleverTapEventTypes = {
  onUserLoginPush: "onUserLoginPush",
  eventPush: "eventPush",
  profilePush: "profilePush",
  splTrackEvent: "splTrackEvent",
  getPlatform: "getPlatform",
};

type RegistrationComponentProps = {
  flowType?: typeof regFlow | typeof sprFlow | typeof sprWithJobIdFlow;
  jobDetailUrlForSprWithId?: string;
  hostname: string;
};
type NextStepType = {
  nextAction: string;
  profileProgress: number;
  isSkipable: boolean;
};

// SPR Header component for progress and skip functionality
const SPRProgressHeader = () => {
  const { screenType, isSkipabl, progressPercent } = useRegistrationProps();

  // Computed header state logic
  const headerState = useMemo(() => {
    const isLoaded = !!screenType;
    const isFirstScreen = screenType === "MOBILE_NUMBER_EMAIL";
    const hasProgress = !!progressPercent && progressPercent !== 100;
    const shouldShowSkip =
      isLoaded && isSkipabl && !isFirstScreen && screenType !== "RESUME";

    return {
      isLoaded,
      hasProgress: isLoaded && hasProgress,
      shouldShowSkip,
    };
  }, [screenType, progressPercent, isSkipabl]);

  // Don't render if no progress to show
  if (!headerState.isLoaded || !headerState.hasProgress) {
    return null;
  }

  return (
    <div className="relative z-50 w-full">
      <div
        className={`px-4 py-3 md:px-0 ${headerState.shouldShowSkip ? "mt-0" : "mt-4"}`}
      >
        <div className="flex flex-col gap-3">
          <div className="mb-4">
            <ProgressBar percent={progressPercent} />
          </div>
        </div>
      </div>
    </div>
  );
};

const RegistrationComponent: React.FC<RegistrationComponentProps> = ({
  flowType = regFlow,
  jobDetailUrlForSprWithId = "",
  hostname,
}) => {
  const [devMode, setDevMode] = useState(false);
  const [toast, setToast] = useState({ show: false, type: "", message: "" });
  const [formLevel, setFormLevel] = useState(0); // Comment the code after use
  const [screenType, setScreenType] = useState("");
  const [details, setDetails] = useState<Details>({});
  const [isSkipabl, setIsSkipabl] = useState(false);
  const [progressPercent, setProgressPercent] = useState(75); // no need handle in using details
  const [isJobApplied, setIsJobApplied] = useState(false);
  const [isTriumph, setIsTriumph] = useState(false);
  const [isApp, setIsApp] = useState<boolean>(false);
  const { fetchData: fetchAPI } = useNetwork();
  const [isLoading, setIsLoading] = useState(false);
  const [isScreenLoading, setIsScreenLoading] = useState(false);
  const isMobile = useMobile();
  const defaultErrorMessage = translation.errorMessages.default;

  const handleEventTracking = async (event: EventTrackingProps) => {
    const search = window.location.search;
    let urlParams = new URLSearchParams(search);
    const isApp = urlParams.get("app");
    const { eventName, eventType, eventProps, action } = event;
    if (eventProps) {
      eventProps["page_name"] = "Registration";
      if (isApp) {
        eventProps["source"] = "APP";
      }
    }
    // Handle Flutter message if authData exists
    if (
      eventType === cleverTapEventTypes.onUserLoginPush &&
      eventProps?.authData
    ) {
      try {
        if (typeof window.MessageHandler?.postMessage === "function") {
          window.MessageHandler.postMessage(
            JSON.stringify({
              type: "AUTH_TOKEN",
              data: eventProps.authData,
            }),
          );
        }
      } catch (error) {
        console.error("Error sending data to Flutter:", error);
      }
    }
    // Create clean eventProps without authData for Clevertap
    const clevertapProps = eventProps ? { ...eventProps } : {};
    if (clevertapProps.authData) {
      delete clevertapProps.authData;
    }
    switch (eventType) {
      case cleverTapEventTypes.eventPush:
        trackClevertapEvents.eventPush(eventName, clevertapProps);
        break;
      case cleverTapEventTypes.onUserLoginPush:
        trackClevertapEvents.onuserLoginPush(clevertapProps);
        break;
      case cleverTapEventTypes.profilePush:
        trackClevertapEvents.profilePush(clevertapProps);
        break;
      case cleverTapEventTypes.splTrackEvent:
        trackClevertapEvents.splTrackEvent(action, eventName, clevertapProps);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    const queryString = window.location.search;
    const params = new URLSearchParams(queryString);
    const triumphParam = params.get("isTriumph");
    const isAppActive: boolean = params.get("app") === "true";
    triumphParam && setIsTriumph(true);
    isAppActive && setIsApp(isAppActive);

    // For dev purpose only will remove this code
    const devModeParam = params.get("devMode");
    const devEnv = window.location.host.split(".")[0] !== "www";
    devModeParam && devEnv && setDevMode(true);

    trackPerformance();
  }, []);

  const handleOnSubmit = async (
    data?: any,
    nextAction?: string,
    trackingObj?: any,
  ) => {
    setIsLoading(true);
    if (!data) return;
    const payload = { data, currentAction: screenType };
    const param = new URLSearchParams({
      ...(isJobApplied && { jobApplied: "true" }),
      ...(isTriumph && { isTriumph: "true" }),
    }).toString();

    try {
      const addDetailsResponse = (await fetchAPI(
        `${API_ENDPOINT(hostname).middleware.addDetails}${param ? `?${param}` : ""}`,
        "POST",
        {},
        payload,
      )) as AddDetailsAPIResDataType;

      const response = addDetailsResponse?.data;
      const accessToken = `${response?.data?.accessToken}::bearer:`;

      if (!response) {
        handleError(addDetailsResponse);
        return;
      }
      if (screenType === "MOBILE_NUMBER_EMAIL" && data) {
        trackLeadGenEvents(data, response);
      }

      nextAction = nextAction || response.nextAction;
      const progressPercent = response.profileProgress ?? 0;
      const skipable = response.isSkipable;

      if (nextAction === "SPECIALISATION" && response?.prefill) {
        setQualificationId(response.prefill.highestQualification.id);
      }
      if (nextAction === "END_DATE" && response?.prefill) {
        setDetails((prev) => ({
          ...prev,
          jobStartDate: response?.prefill?.startDate,
        }));
      }
      if (response.message === "Successfully submitted!!") {
        if (nextAction) {
          handleScreenChange(nextAction, progressPercent, skipable);
        } else {
          if (progressPercent === 100) {
            handleScreenChange("SUCCESS", progressPercent, skipable);
          }
        }
        if (trackingObj && Object.keys(trackingObj).length) {
          handleEventTracking({
            eventType: "eventPush",
            eventName: "Registration_Filled",
            eventProps: trackingObj,
          });
        }
        if (
          trackingObj &&
          (trackingObj as { source_name?: string })?.source_name ===
            "CONVERSATIONAL_REG"
        ) {
          trackingObj["splEventProps"] = {
            ...trackingObj?.splEventProps,
            accessToken,
          };
          handleEventTracking({
            eventType: "splTrackEvent",
            eventName: "REGISTRATION",
            eventProps: (trackingObj as any).splEventProps,
            action: "PAGE_SUBMIT",
          });
        }
      }
    } catch (error) {
      setToast({ show: true, type: "error", message: defaultErrorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const getNextAction = async (customCurrentStep?: string) => {
    const isCustomStep = typeof customCurrentStep === "string";
    const currentAction = isCustomStep ? customCurrentStep : screenType;
    try {
      const response = await fetchAPI(
        `${API_ENDPOINT(hostname).middleware.nextAction}?currentStep=${currentAction}${isJobApplied ? "&jobApplied=true" : ""}${isTriumph ? "&isTriumph=true" : ""}`,
        "GET",
      );
      const { nextAction, profileProgress, isSkipable } =
        response.data as NextStepType;

      if (!nextAction && profileProgress === 100) {
        handleScreenChange("SUCCESS", profileProgress, isSkipable);
        return;
      }

      if (nextAction === "SPECIALISATION" && response?.data?.prefill) {
        setQualificationId(response.data.prefill.highestQualification.id);
      }
      handleScreenChange(nextAction, profileProgress, isSkipable);
    } catch (error) {
      setToast({ show: true, type: "error", message: defaultErrorMessage });
    }
  };

  const handleError = (addDetailsResponse: AddDetailsAPIResDataType) => {
    if (addDetailsResponse.error) {
      const { error = defaultErrorMessage, status = "" } = JSON.parse(
        Object(addDetailsResponse.error),
      );

      if (status) setDetails((prev) => ({ ...prev, status }));
      setToast({
        show: true,
        type: "error",
        message:
          status === 408
            ? defaultErrorMessage
            : error?.message?.errorMessage || defaultErrorMessage,
      });
    } else {
      setToast({
        show: true,
        type: "error",
        message: defaultErrorMessage,
      });
    }
  };

  const trackLeadGenEvents = (data: any, response: any) => {
    let registerCtProps = {
      Email: data.email,
      Phone: `+${data.mobileNumber.countryCode}${data.mobileNumber.mobileNumber}`,
      whatsapp_toggle_status: data.whatsappOptedIn,
      signup_via: data.socialAccessToken ? "SocialLogin" : "Email",
      authData: response?.data,
    };
    handleEventTracking({
      eventType: "onUserLoginPush",
      eventName: "Registration_Filled",
      eventProps: registerCtProps,
    });
    if (data.socialProvider) {
      handleEventTracking({
        eventType: "eventPush",
        eventName: "Registration_Filled",
        eventProps: {
          Registration_via_social: data.socialProvider,
        },
      });
    }
    sessionStorage.setItem("emailId", data.email);
  };

  const setQualificationId = (id: string) => {
    setDetails((prev) => ({ ...prev, qualificationId: id }));
  };

  const handleScreenChange = async (
    nextAction: string,
    profileProgress?: number,
    isSkipabl?: boolean,
    data?: any,
  ) => {
    setIsScreenLoading(true);
    if (nextAction === "SUCCESS") {
      const response = await fetchAPI(
        `${API_ENDPOINT(hostname).middleware.profileDetails}?fields=personal_details,resumes`,
        "GET",
      );

      if (response.data) {
        setDetails((prev) => ({
          ...prev,
          personalDetails: {
            ...(response.data?.personalDetailSection?.personalDetails ?? {}),
          },
          resume: { ...(response.data?.resumeSection?.resume ?? {}) },
        }));
      }
    } else {
      setDetails((prev) => (data ? { ...prev, ...data } : prev));
    }

    setScreenType(nextAction);
    setProgressPercent(profileProgress ?? 0);
    setIsSkipabl(isSkipabl ?? false);
    setIsScreenLoading(false);
  };

  useEffect(() => {
    if (isJobApplied) {
      getNextAction();
    }
  }, [isJobApplied]);

  // Only for dev purpose
  useEffect(() => {
    handleFormLevel(0);
  }, []);

  const handleFormLevel = (level: number) => {
    // Comment the code after use
    const key = Object.keys(regMap)[level];
    if (level >= 0 && level < Object.keys(regMap).length) {
      setScreenType(key);
      setFormLevel(level);
      setProgressPercent(level * 5);
    }
  };

  const contextValue = useMemo(
    () => ({
      details,
      screenType,
      isLoading,
      setIsLoading,
      isSkipabl,
      translation,
      progressPercent,
      setToast,
      setDetails,
      getNextAction,
      handleOnSubmit,
      isJobApplied,
      setIsJobApplied,
      handleEventTracking,
      handleScreenChange,
      isApp,
      flowType,
      jobDetailUrlForSprWithId,
      hostname,
    }),
    [
      details,
      progressPercent,
      isSkipabl,
      handleEventTracking,
      handleOnSubmit,
      getNextAction,
      isApp,
      hostname,
    ],
  );

  return (
    <RegistrationContext.Provider value={contextValue}>
      <div
        className={twMerge(
          "relative flex w-screen max-w-full flex-col",
          flowType !== sprWithJobIdFlow
            ? "bg-surface-background-grey"
            : isMobile
              ? "bg-surface-background-grey"
              : "",
          flowType === sprWithJobIdFlow
            ? isMobile
              ? "min-h-screen"
              : "h-full"
            : isMobile
              ? "h-[100svh]"
              : "min-h-screen",
        )}
      >
        <div
          className={`left-1/2 top-[-60%] z-0 h-[928px] rounded-full border opacity-[13%] ${flowType === sprWithJobIdFlow ? "absolute w-full" : "fixed w-[926px]"}`}
          style={{
            backgroundImage:
              "linear-gradient(90deg, rgba(255, 185, 71, 0.5) 1.86%, rgba(112, 0, 255, 0.44) 100.99%, rgba(112, 0, 255, 0.44) 101%)",
            filter: "blur(200px)",
            transform: "translateX(-50%) rotate(-180deg)",
          }}
        />
        {flowType !== sprWithJobIdFlow && (
          <div className="fixed bottom-0 right-0 z-0">
            <Image
              src="/seeker-profile/wavylines.webp"
              alt="Wavy Line"
              width={700}
              height={700}
              priority={false}
              className="object-contain"
            />
          </div>
        )}
        {(flowType !== sprWithJobIdFlow || isMobile) && !isApp && <Header />}
        {flowType === sprWithJobIdFlow && !isMobile && <SPRProgressHeader />}
        {devMode && (
          <div className="absolute bottom-3 left-4 z-50 flex gap-2 text-2xl">
            <button onClick={() => handleFormLevel(formLevel - 1)}>
              {"<<"}
            </button>
            <span>{formLevel}</span>
            <button onClick={() => handleFormLevel(formLevel + 1)}>
              {">>"}
            </button>
          </div>
        )}
        <Toast
          position="top-center"
          showToast={toast.show}
          text={toast.message}
          type={toast.type}
          width={isMobile ? "75" : "25"}
          timer={3000}
          onClose={() => setToast((prev) => ({ ...prev, show: false }))}
        />
        {isScreenLoading ? (
          <Skeleton screenType={screenType} isMobile={isMobile} />
        ) : (
          <Suspense
            fallback={<Skeleton screenType={screenType} isMobile={isMobile} />}
          >
            {screenType && screenType === "MOBILE_NUMBER_EMAIL" ? (
              <SignUp />
            ) : (
              regMap[screenType] && React.createElement(regMap[screenType])
            )}
          </Suspense>
        )}
      </div>
    </RegistrationContext.Provider>
  );
};

const Registration = React.memo(RegistrationComponent);
export default Registration;
